import { Link } from 'react-router-dom'
import { ArrowRight, Phone } from 'lucide-react'
import Section from '../components/layout/Section'
import Reveal from '../components/layout/Reveal'
import Seo from '../components/seo/Seo'
import { useBreadcrumbs } from '../seo/useBreadcrumbs'
import { faqSchema } from '../seo/schema'

interface FaqItem {
  question: string
  answer: React.ReactNode
  /** Plain-text version for JSON-LD (must match what's visibly on the page). */
  answerText: string
}

const CATEGORIES: { name: string; items: FaqItem[] }[] = [
  {
    name: 'Buying at the Beach',
    items: [
      {
        question: 'How do I start buying a beach house in Delaware?',
        answer: (
          <>
            Start with a conversation about how you&apos;ll use the home — primary, second home, or
            investment — because it determines which towns, property types, and loan structures fit.
            Our <Link to="/buy" className="text-accent-gold hover:underline">buyer&apos;s guide</Link> walks
            through the three paths, and the <Link to="/listings" className="text-accent-gold hover:underline">live
            listings map</Link> shows what&apos;s actually on the market today.
          </>
        ),
        answerText:
          'Start with a conversation about how you\'ll use the home — primary, second home, or investment — because it determines which towns, property types, and loan structures fit. Our buyer\'s guide walks through the three paths, and the live listings map shows what\'s actually on the market today.',
      },
      {
        question: 'Which Delaware beach town is right for me?',
        answer: (
          <>
            It depends on your rhythm: <Link to="/areas/lewes" className="text-accent-gold hover:underline">Lewes</Link> for
            historic walkability, <Link to="/areas/rehoboth-beach" className="text-accent-gold hover:underline">Rehoboth
            Beach</Link> for boardwalk energy and dining, <Link to="/areas/dewey-beach" className="text-accent-gold hover:underline">Dewey</Link> for
            beach-and-bay condo living, <Link to="/areas/bethany-beach" className="text-accent-gold hover:underline">Bethany</Link> and{' '}
            <Link to="/areas/fenwick-island" className="text-accent-gold hover:underline">Fenwick Island</Link> for
            the Quiet Resorts pace, and <Link to="/areas/milton" className="text-accent-gold hover:underline">Milton</Link> for
            value just inland. We tour buyers through all of them — most people know within a day.
          </>
        ),
        answerText:
          'It depends on your rhythm: Lewes for historic walkability, Rehoboth Beach for boardwalk energy and dining, Dewey for beach-and-bay condo living, Bethany and Fenwick Island for the Quiet Resorts pace, and Milton for value just inland. We tour buyers through all of them — most people know within a day.',
      },
      {
        question: 'What should I know about flood zones before buying at the beach?',
        answer:
          'Flood zones materially affect insurance costs and lending at the Delaware beaches. We pull the flood zone determination, review elevation certificates where they exist, and get actual insurance quotes during due diligence — before you\'re committed. Newer elevated construction often carries surprisingly modest premiums; older slab-on-grade cottages can be the reverse.',
        answerText:
          'Flood zones materially affect insurance costs and lending at the Delaware beaches. We pull the flood zone determination, review elevation certificates where they exist, and get actual insurance quotes during due diligence — before you\'re committed. Newer elevated construction often carries surprisingly modest premiums; older slab-on-grade cottages can be the reverse.',
      },
      {
        question: 'Is it better to buy new construction or an older beach cottage?',
        answer:
          'Both work at the Delaware beaches, and the trade-offs are real: new construction offers amenities, elevation, and low near-term maintenance, while older cottages offer in-town locations, lot value, and character — with renovation risk. Shawn\'s builder and historic-restoration background lets us evaluate either honestly, including what an older home will really cost to update.',
        answerText:
          'Both work at the Delaware beaches, and the trade-offs are real: new construction offers amenities, elevation, and low near-term maintenance, while older cottages offer in-town locations, lot value, and character — with renovation risk. Shawn\'s builder and historic-restoration background lets us evaluate either honestly, including what an older home will really cost to update.',
      },
    ],
  },
  {
    name: 'Selling',
    items: [
      {
        question: 'What is my Delaware beach home worth?',
        answer: (
          <>
            Beach values are hyper-local — ocean-block vs. off-beach, rental history, flood zone, and
            community amenities move prices far more than square footage. We prepare valuations from
            actual comparable sales in your specific community, not algorithms. Request one through
            our <Link to="/sell" className="text-accent-gold hover:underline">seller&apos;s page</Link> — free
            and no obligation.
          </>
        ),
        answerText:
          'Beach values are hyper-local — ocean-block vs. off-beach, rental history, flood zone, and community amenities move prices far more than square footage. We prepare valuations from actual comparable sales in your specific community, not algorithms. Request one through our seller\'s page — free and no obligation.',
      },
      {
        question: 'When is the best season to list at the Delaware beaches?',
        answer:
          'Buyer traffic typically peaks from early spring through summer when second-home buyers are in town, but serious buyers shop year-round and low-inventory winter stretches can favor sellers. The honest answer depends on your property type and town — we\'ll show you actual seasonal sales patterns before you choose a date.',
        answerText:
          'Buyer traffic typically peaks from early spring through summer when second-home buyers are in town, but serious buyers shop year-round and low-inventory winter stretches can favor sellers. The honest answer depends on your property type and town — we\'ll show you actual seasonal sales patterns before you choose a date.',
      },
      {
        question: 'Should I make repairs before listing?',
        answer:
          'Targeted preparation usually pays; full renovation rarely does. We walk every listing with a builder\'s eye and identify which repairs return their cost, which issues inspectors will flag regardless, and which projects to skip. Sometimes the right answer is pricing for condition instead of renovating.',
        answerText:
          'Targeted preparation usually pays; full renovation rarely does. We walk every listing with a builder\'s eye and identify which repairs return their cost, which issues inspectors will flag regardless, and which projects to skip. Sometimes the right answer is pricing for condition instead of renovating.',
      },
    ],
  },
  {
    name: 'Taxes & Costs',
    items: [
      {
        question: 'What is the Delaware realty transfer tax?',
        answer:
          'Delaware charges a realty transfer tax of 4% of the sale price, customarily split equally between buyer and seller — typically 2% each. Rates and local practice can change, so confirm the current rate and your negotiated split at contract time. It\'s the one significant transaction tax in an otherwise low-tax state.',
        answerText:
          'Delaware charges a realty transfer tax of 4% of the sale price, customarily split equally between buyer and seller — typically 2% each. Rates and local practice can change, so confirm the current rate and your negotiated split at contract time. It\'s the one significant transaction tax in an otherwise low-tax state.',
      },
      {
        question: 'How low are Delaware property taxes, really?',
        answer:
          'Sussex County property taxes are widely considered among the lowest in the Mid-Atlantic — often a fraction of what the same home would carry in New Jersey or Maryland. Rather than quote averages that go stale, we pull actual tax records for any property you\'re considering. Combined with no sales tax, the carrying-cost advantage is substantial.',
        answerText:
          'Sussex County property taxes are widely considered among the lowest in the Mid-Atlantic — often a fraction of what the same home would carry in New Jersey or Maryland. Rather than quote averages that go stale, we pull actual tax records for any property you\'re considering. Combined with no sales tax, the carrying-cost advantage is substantial.',
      },
      {
        question: 'What are typical closing costs when buying at the beach?',
        answer:
          'Beyond your transfer-tax share, expect lender fees, title insurance, recording fees, and prepaid items like insurance and tax escrows — plus flood insurance where required. We prepare a full closing-cost estimate before you make an offer, so the settlement sheet never holds surprises.',
        answerText:
          'Beyond your transfer-tax share, expect lender fees, title insurance, recording fees, and prepaid items like insurance and tax escrows — plus flood insurance where required. We prepare a full closing-cost estimate before you make an offer, so the settlement sheet never holds surprises.',
      },
    ],
  },
  {
    name: 'Rentals & Investment',
    items: [
      {
        question: 'Can I rent out my beach house when I\'m not using it?',
        answer:
          'Often yes — the summer weekly-rental market is mature — but every town licenses rentals differently and many HOAs restrict or prohibit short-term rentals. If rental income is part of your plan, say so up front: we\'ll only show you properties where it\'s allowed and share actual rental comps rather than projections.',
        answerText:
          'Often yes — the summer weekly-rental market is mature — but every town licenses rentals differently and many HOAs restrict or prohibit short-term rentals. If rental income is part of your plan, say so up front: we\'ll only show you properties where it\'s allowed and share actual rental comps rather than projections.',
      },
      {
        question: 'What kind of rental income can a Delaware beach property generate?',
        answer:
          'It varies enormously by town, proximity to the beach, size, and amenities — and the season is concentrated in summer weeks. We never quote income from pro formas; we pull actual rental histories for comparable properties so you can underwrite with real numbers. Dewey condos, Sea Colony units, and in-town Rehoboth cottages all behave differently.',
        answerText:
          'It varies enormously by town, proximity to the beach, size, and amenities — and the season is concentrated in summer weeks. We never quote income from pro formas; we pull actual rental histories for comparable properties so you can underwrite with real numbers. Dewey condos, Sea Colony units, and in-town Rehoboth cottages all behave differently.',
      },
      {
        question: 'Do I need a rental license at the Delaware beaches?',
        answer:
          'In most towns, yes — the City of Rehoboth Beach, Town of Dewey Beach, Town of Bethany Beach, and others each license and regulate rentals with their own fees and occupancy rules, and unincorporated areas follow county rules. We confirm the exact requirements for any address before you buy.',
        answerText:
          'In most towns, yes — the City of Rehoboth Beach, Town of Dewey Beach, Town of Bethany Beach, and others each license and regulate rentals with their own fees and occupancy rules, and unincorporated areas follow county rules. We confirm the exact requirements for any address before you buy.',
      },
    ],
  },
  {
    name: 'About Us',
    items: [
      {
        question: 'Who are Shawn McDonnell and Merritt Cooch?',
        answer: (
          <>
            We&apos;re a husband-and-wife real estate team with the Carrie Lingo Team at Jack Lingo
            REALTOR® in Lewes. Shawn is an Associate Broker with a builder&apos;s and historic
            restorer&apos;s background; Merritt is an 11th-generation Delawarean. Read our full story
            on the <Link to="/about" className="text-accent-gold hover:underline">About page</Link>.
          </>
        ),
        answerText:
          'We\'re a husband-and-wife real estate team with the Carrie Lingo Team at Jack Lingo REALTOR® in Lewes. Shawn is an Associate Broker with a builder\'s and historic restorer\'s background; Merritt is an 11th-generation Delawarean. Read our full story on the About page.',
      },
      {
        question: 'Why should I work with a local team instead of searching the portals?',
        answer:
          'The portals are fine for browsing — our own listings map runs on the same Bright MLS feed. What portals can\'t tell you is which streets flood, which HOAs ban rentals, which cottages are teardowns, and which listings are mispriced. Local knowledge is the product; the search box is free.',
        answerText:
          'The portals are fine for browsing — our own listings map runs on the same Bright MLS feed. What portals can\'t tell you is which streets flood, which HOAs ban rentals, which cottages are teardowns, and which listings are mispriced. Local knowledge is the product; the search box is free.',
      },
      {
        question: 'What areas do you serve?',
        answer: (
          <>
            Lewes, Rehoboth Beach, Dewey Beach, Bethany Beach, Fenwick Island, and Milton — see
            our <Link to="/areas" className="text-accent-gold hover:underline">town guides</Link> for
            the neighborhoods, lifestyle, and market character of each.
          </>
        ),
        answerText:
          'Lewes, Rehoboth Beach, Dewey Beach, Bethany Beach, Fenwick Island, and Milton — see our town guides for the neighborhoods, lifestyle, and market character of each.',
      },
    ],
  },
]

export default function Faq() {
  const crumbs = useBreadcrumbs([{ label: 'FAQ', path: '/faq' }])
  const allItems = CATEGORIES.flatMap((c) => c.items)
  return (
    <div className="bg-neutral-charcoal">
      <Seo
        title="Delaware Beach Real Estate FAQs | Taxes, Rentals, Buying & Selling"
        description="Answers on Delaware transfer tax, property taxes, flood zones, rental licenses, and buying or selling at the Delaware beaches — from local Lewes agents Shawn & Merritt."
        path="/faq"
        jsonLd={[faqSchema(allItems.map(({ question, answerText }) => ({ question, answer: answerText }))), crumbs.jsonLd]}
      />

      <Section className="bg-neutral-charcoal pt-28 pb-16">
        {crumbs.node}
        <Reveal>
          <div className="mb-12 max-w-3xl">
            <p className="font-display text-[12px] uppercase tracking-[0.3em] font-medium text-white/70">
              Straight Answers
              <span className="block w-6 h-[2px] bg-accent-gold mt-3" aria-hidden="true" />
            </p>
            <h1 className="mt-4 font-display uppercase text-white leading-none text-[clamp(36px,5.5vw,72px)]">
              <span className="font-thin">Delaware Beach Real Estate </span>
              <span className="font-bold">FAQs</span>
            </h1>
            <p className="mt-4 text-lg text-white/70 font-light leading-[1.9]">
              The questions we hear every week — answered the way we&apos;d
              answer them across a kitchen table.
            </p>
          </div>
        </Reveal>

        <div className="max-w-3xl space-y-14">
          {CATEGORIES.map((cat) => (
            <Reveal key={cat.name}>
              <h2 className="font-display text-lg font-bold uppercase tracking-[0.2em] text-accent-gold mb-6">
                {cat.name}
              </h2>
              <div className="space-y-3">
                {cat.items.map((f) => (
                  <details
                    key={f.question}
                    className="group border border-white/10 bg-white/5 open:border-accent-gold/40"
                  >
                    <summary className="cursor-pointer list-none p-5 font-display text-sm font-bold uppercase tracking-wide text-white flex items-center justify-between gap-4 [&::-webkit-details-marker]:hidden">
                      {f.question}
                      <span className="text-accent-gold text-xl leading-none transition-transform group-open:rotate-45" aria-hidden="true">
                        +
                      </span>
                    </summary>
                    <p className="px-5 pb-5 text-white/70 font-light leading-[1.9]">{f.answer}</p>
                  </details>
                ))}
              </div>
            </Reveal>
          ))}
        </div>
      </Section>

      {/* CTA */}
      <section className="bg-accent-gold py-16">
        <div className="max-w-6xl mx-auto px-6 flex flex-col md:flex-row md:items-center md:justify-between gap-8">
          <div>
            <h2 className="font-display uppercase text-primary-ink leading-none text-[clamp(26px,3.5vw,44px)]">
              <span className="font-thin">Didn&apos;t find </span>
              <span className="font-bold">your answer?</span>
            </h2>
            <p className="mt-3 text-primary-ink/80 max-w-xl">
              Ask us directly — you&apos;ll get a straight answer from people
              who work these streets every day.
            </p>
          </div>
          <div className="flex flex-col sm:flex-row gap-4 shrink-0">
            <Link
              to="/contact"
              className="inline-flex items-center justify-center gap-2 bg-primary-ink text-white px-8 py-4 text-sm font-display font-bold uppercase tracking-[0.15em] hover:bg-neutral-charcoal transition-colors"
            >
              Contact Us <ArrowRight className="h-4 w-4" aria-hidden="true" />
            </Link>
            <a
              href="tel:+13026452207"
              className="inline-flex items-center justify-center gap-2 border-2 border-primary-ink text-primary-ink px-8 py-4 text-sm font-display font-bold uppercase tracking-[0.15em] hover:bg-primary-ink hover:text-white transition-colors"
            >
              <Phone className="h-4 w-4" aria-hidden="true" /> 302-645-2207
            </a>
          </div>
        </div>
      </section>
    </div>
  )
}
